import { ReactNode, useEffect } from 'react';
import { APOLLO_ERROR_POLICY, APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { alpha } from '@mui/material/styles';
import { slate } from '@/lib/colours';
import { Box, CircularProgress, Stack, Typography } from '@/primitives';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import {
  useGetSalesLazyQuery,
  useGetSocietyEbvLogoLazyQuery,
} from '@/publicSearch/graphql/queries.generated';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useIsEbvEnabled } from '@/hooks/publicSearch/useIsEbvEnabled';
import { useDocumentImageWithSociety } from '@/hooks/publicSearch/useDocumentImageWithSociety';
import { FieldOption, OperatorOption } from '../constants';
import { formatDate } from '@/lib/dateTime';
import { useSocietyDateFormat } from '@/hooks/publicSearch/useSocietyDisplayOptions';
import ImageWithSkeleton from '@/components/utils/ImageWithSkeleton';
import { normalizeExternalHref } from '@/utils/externalHref';
import { LinkifiedText } from './LinkifiedText';
import { linkifyText, saleEnquirySubject } from './saleLinks';
import type {
  ComparisonOperator,
  SaleFilterField,
} from '@/graphqlClient/types';

const KeyVal = ({
  label,
  content,
}: {
  label: ReactNode;
  content: ReactNode;
}) => (
  <Stack sx={{ gap: 0.5 }}>
    <Typography
      sx={{ color: slate[400], fontSize: { xs: '12px', sm: '14px' } }}
    >
      {label}
    </Typography>
    <Box sx={{ fontSize: { xs: '14px' } }}>{content}</Box>
  </Stack>
);

const Section = (props: {
  children: ReactNode;
  title: ReactNode;
  noBorder?: boolean;
}) => {
  const { children, title, noBorder } = props;

  return (
    <Stack
      sx={{
        gap: { xs: 1, sm: 2 },
        borderBottom: noBorder
          ? undefined
          : `1px solid ${alpha(slate[600], 0.2)}`,
        py: { xs: 1.5, sm: 2 },
      }}
    >
      <Typography
        variant="bodylarge"
        sx={{
          color: 'text.secondary',
          fontSize: { xs: '13px', sm: undefined },
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          py: { xs: 0.5, sm: 2 },
        }}
      >
        {children}
      </Stack>
    </Stack>
  );
};

export default function SaleNote(props: { saleId: number }) {
  const { saleId } = props;
  const [getSales, { data, loading }] = useGetSalesLazyQuery({
    fetchPolicy: APOLLO_FETCH_POLICY.NO_CACHE,
    errorPolicy: APOLLO_ERROR_POLICY.ALL,
  });
  const [getSocietyEbvLogo, { data: ebvLogoData, loading: loadingEbvLogo }] =
    useGetSocietyEbvLogoLazyQuery({
      fetchPolicy: APOLLO_FETCH_POLICY.NO_CACHE,
    });
  const society = usePublicSearchContext((context) => context.society);
  const dateFormat = useSocietyDateFormat();
  const isEbvEnabled = useIsEbvEnabled();
  const orgId = society.orgId;
  const societyId = society.societyId ?? orgId ?? '';
  const saleNote = data?.sales.items?.[0] || null;

  const ebvLogoUrl =
    ebvLogoData?.societies?.items?.[0]?.settings?.[0]?.url ?? null;
  const { imgSrc: ebvLogoSrc, isLoading: ebvLogoImageLoading } =
    useDocumentImageWithSociety(ebvLogoUrl, societyId);

  const agent1ImageUrl = saleNote?.agent1?.imageUrl ?? null;
  const { imgSrc: agent1ImgSrc } = useDocumentImageWithSociety(
    agent1ImageUrl,
    societyId,
  );

  const agent2ImageUrl = saleNote?.agent2?.imageUrl ?? null;
  const { imgSrc: agent2ImgSrc } = useDocumentImageWithSociety(
    agent2ImageUrl,
    societyId,
  );

  useEffect(() => {
    getSales({
      variables: {
        requestInput: {
          pagination: {
            currentPage: 1,
            pageSize: 1,
          },
          filterOptions: [
            {
              field: FieldOption.saleId.value as SaleFilterField,
              operator: OperatorOption.equals.value as ComparisonOperator,
              value: saleId.toString(),
            },
            {
              field: FieldOption.societyId.value as SaleFilterField,
              operator: OperatorOption.equals.value as ComparisonOperator,
              value: orgId.toString(),
            },
          ],
        },
      },
      context: {
        headers: setDefaultHeader(orgId),
      },
    });
  }, [orgId, saleId, getSales]);

  useEffect(() => {
    if (!isEbvEnabled || !orgId) return;
    getSocietyEbvLogo({
      variables: {
        societyId: orgId.toString(),
      },
      context: {
        headers: setDefaultHeader(orgId),
      },
    });
  }, [isEbvEnabled, orgId, getSocietyEbvLogo]);

  if (loading) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          py: 10,
        }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  if (!saleNote) {
    return <Typography sx={{ p: 2 }}>No Sale Note found.</Typography>;
  }

  const agent1Href = normalizeExternalHref(saleNote.agent1?.agentUrl);
  const agent2Href = normalizeExternalHref(saleNote.agent2?.agentUrl);

  return (
    <>
      <Box sx={{ px: { xs: 2, sm: 0 }, pt: { xs: 1, sm: 0 } }}>
        <Typography sx={{ fontSize: { xs: '14px' } }}>
          {saleNote?.saleName}
        </Typography>
        <Typography variant="headlinesmall" sx={{ fontSize: { xs: '14px' } }}>
          {saleNote?.saleDateDescription}
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 0 } }}>
        <Section title="Sale Details">
          <Box sx={{ flex: 1 }}>
            <KeyVal label="Location" content={saleNote?.saleLocation} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <KeyVal
              label="Sale Date"
              content={formatDate(new Date(saleNote?.saleDate), dateFormat)}
            />
          </Box>
        </Section>

        <Section title="Notes" noBorder>
          <Typography
            component="pre"
            sx={{
              fontSize: { xs: '14px' },
              fontFamily: 'inherit',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              m: 0,
            }}
          >
            {/* BLS-406: URLs kept by RPT.f_StripHtmlNotes ("click here (https://...)"). */}
            <LinkifiedText
              segments={linkifyText(
                saleNote?.saleNotes,
                saleEnquirySubject(saleNote?.saleName),
              )}
            />
          </Typography>
        </Section>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3,
          px: { xs: 2, sm: 0 },
          py: { xs: 2, sm: 3 },
          mt: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
        }}
      >
        {isEbvEnabled && ebvLogoUrl && (
          <Box
            onClick={() => {
              const settingUrl =
                ebvLogoData?.societies?.items?.[0]?.settings?.[0]?.settingUrl;
              if (settingUrl) {
                window.location.href = settingUrl;
              }
            }}
            sx={{
              cursor: ebvLogoData?.societies?.items?.[0]?.settings?.[0]
                ?.settingUrl
                ? 'pointer'
                : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ImageWithSkeleton
              src={ebvLogoSrc ?? ''}
              alt={
                ebvLogoData?.societies?.items?.[0]?.settings?.[0]?.toolTip ||
                'EBV Logo'
              }
              width={50}
              height={100}
              isLoading={loadingEbvLogo || ebvLogoImageLoading}
            />
          </Box>
        )}

        {saleNote?.agent1?.imageUrl && (
          <Box
            onClick={() => {
              if (agent1Href) {
                window.open(agent1Href, '_blank', 'noopener,noreferrer');
              }
            }}
            sx={{
              cursor: agent1Href ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ImageWithSkeleton
              src={agent1ImgSrc ?? ''}
              alt={saleNote.agent1.agentName || 'Agent 1 Logo'}
              width={120}
              height={50}
              isLoading={!agent1ImgSrc && !!agent1ImageUrl}
            />
          </Box>
        )}

        {saleNote?.agent2?.imageUrl && (
          <Box
            onClick={() => {
              if (agent2Href) {
                window.open(agent2Href, '_blank', 'noopener,noreferrer');
              }
            }}
            sx={{
              cursor: agent2Href ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ImageWithSkeleton
              src={agent2ImgSrc ?? ''}
              alt={saleNote.agent2.agentName || 'Agent 2 Logo'}
              width={100}
              height={50}
              isLoading={!agent2ImgSrc && !!agent2ImageUrl}
            />
          </Box>
        )}
      </Box>
    </>
  );
}
